import { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import type { components } from "../api/schema";
import { useWorkingScope, type WorkingScopeSearch } from "../lib/workingScope";
import { apiClient } from "../lib/apiClient";
import { ApiRequestError } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import { markerReferenceWarningDescription } from "../lib/markerReferenceWarnings";
import { Button, EmptyState, Page, PageHeader, useToast } from "../ui";
import { AgentScopePicker } from "./AgentScopeSelect";
import {
  MarkerAuthoringForm,
  type JudgeModelPolicy,
  type MarkerAuthoringSubmission,
} from "./MarkerAuthoringForm";
import { MarkerAuthoringLeaveGuard } from "./markerAuthoringLeaveGuard";

type MarkerCreateBody = components["schemas"]["MarkerCreate"];

// /markers/new — the reference Authoring route (UI-LAW §4c). Success and
// Cancel return to /markers preserving ONLY the root-owned `agent` param, so
// a stale `?marker=` drawer subject never re-opens after the round trip.

// Fail-closed policy while /v1/me is pending or unavailable: the judge model
// select renders empty rather than allowing free text.
const unavailableJudgeModels: JudgeModelPolicy = {
  allows_free_text: false,
  model_refs: [],
  model_options: [],
};

export function MarkerCreate() {
  const { agentId } = useWorkingScope();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const me = useQuery({
    enabled: agentId != null,
    queryKey: queryKeys.me,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/me");
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  const [isDirty, setIsDirty] = useState(false);

  const createMarker = useMutation({
    mutationFn: async (body: MarkerCreateBody) => {
      const { data, error } = await apiClient.POST("/v1/markers", { body });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (marker) => {
      queryClient.setQueryData(queryKeys.markers.detail(marker.id), marker);
      await Promise.all([
        // Prefix match: `list()` invalidates the team-wide AND every
        // agent-filtered markers list.
        queryClient.invalidateQueries({ queryKey: queryKeys.markers.list() }),
        agentId
          ? queryClient.invalidateQueries({
              queryKey: queryKeys.markerConfiguration.detail(agentId),
            })
          : Promise.resolve(),
      ]);
      toast.success("Marker created", {
        description: markerReferenceWarningDescription(marker),
      });
      await navigate({
        search: ({ agent, team }: WorkingScopeSearch) => ({ agent, team }),
        to: "/markers",
      });
    },
  });

  function cancel(): void {
    void navigate({
      search: ({ agent, team }: WorkingScopeSearch) => ({ agent, team }),
      to: "/markers",
    });
  }

  async function submit(submission: MarkerAuthoringSubmission): Promise<void> {
    if (submission.mode !== "create" || agentId == null) {
      return;
    }
    await createMarker.mutateAsync({
      ...submission.body,
      agent_id: agentId,
    });
  }

  // Un-scoped arrival is fixed IN PLACE: the body carries the ScopePicker and
  // the sidebar agent selector remains the switcher. Switching agent is a
  // router navigation, so the §4c leave guard below intercepts it on dirty
  // state exactly like Cancel.
  return (
    <Page variant="workspace">
      <MarkerAuthoringLeaveGuard isDirty={isDirty} />
      {agentId == null ? (
        <AgentScopePicker
          emptyState={
            <EmptyState
              action={
                <Button
                  onClick={() => {
                    void navigate({ search: true, to: "/agents" });
                  }}
                >
                  Create agent
                </Button>
              }
              description="Markers evaluate one agent's transcripts."
              title="No agents"
            />
          }
          surfaceIcon="clipboardCheck"
          surfaceLabel="Markers"
        />
      ) : null}
      {agentId == null ? null : (
        <MarkerAuthoringForm
          agentId={agentId}
          judgeModelPolicy={
            me.data?.judge_model_policy ?? unavailableJudgeModels
          }
          // Remount on agent change: in-progress authoring never leaks across
          // agents (the leave guard confirms the discard first).
          key={agentId}
          mode="create"
          renderHeader={(actions) => <PageHeader actions={actions} compact />}
          onCancel={cancel}
          onDirtyChange={setIsDirty}
          onSubmit={submit}
        />
      )}
    </Page>
  );
}
